import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import styles from './careAdvisorDashboard.module.scss';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import Container from '@brands/Components/Container/Container';

/**
 * Care Advisor shell: same global Sidenav + collapsible Sidebar + top bar as Patient/Provider/Collector
 * (`Container`), without the legacy Cases-only sidebar.
 */
const CareAdvisorLayout: React.FC = () => {
  const { userInfo } = useAppSelector(selectAuth);
  const location = useLocation();
  const isMessagesPage = location.pathname.includes('/messages') || location.pathname.includes('/case-chat');

  const isConsultationCaseActivity = /\/consultation-page\/[^/]+\/case-activity\/?$/.test(location.pathname);
  const isConsultationPage =
    location.pathname.includes('/consultation-page') && !isConsultationCaseActivity;
  const isSmileScanReportPage = location.pathname.includes('/smile-scan-report');
  const lowerPath = location.pathname.toLowerCase();
  const isIntakePage =
    lowerPath.includes('/patient-select') ||
    lowerPath.includes('/case-creation/patient/select') ||
    lowerPath.includes('/select-patient') ||
    lowerPath.includes('/select-provider') ||
    lowerPath.includes('/select-provider-details') ||
    lowerPath.includes('/select-care-advisor') ||
    lowerPath.includes('/select-care-advisor-details') ||
    lowerPath.includes('/select-needs') ||
    lowerPath.includes('/select-date-time') ||
    lowerPath.includes('/select-case-type') ||
    lowerPath.includes('/describe-pain-level') ||
    lowerPath.includes('/additional-information') ||
    lowerPath.includes('/upload-image') ||
    lowerPath.includes('/clinical-data') ||
    lowerPath.includes('/payment-information') ||
    lowerPath.includes('/confirm-payment') ||
    lowerPath.includes('/verify-insurance') ||
    lowerPath.includes('/select-dental-insurance') ||
    lowerPath.includes('/create-your-profile') ||
    lowerPath.includes('/select-pharmacy');

  // Intake pages already render their own full-screen container;
  // wrapping them in CareAdvisor shell applies sidebar offsets twice.

  if (
    userInfo?.role?.name !== UserRoleName.CareAdvisor ||
    isMessagesPage ||
    isConsultationPage ||
    isSmileScanReportPage ||
    isIntakePage
  ) {
    return <Outlet />;
  }

  return (
    <Container
      patientDashboard
      isVisibleOnMobile
      childClassName={styles.ccDashboard}
      containerStyle={{
        overflowY: 'hidden',
      }}
      childrenStyle={{
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <div className={styles.dashboardLayout}>
        <div className={styles.contentArea}>
          <Outlet />
        </div>
      </div>
    </Container>
  );
};

export default CareAdvisorLayout;
